import {
  assocPath,
  groupBy,
  has,
  indexBy,
  isEmpty,
  isNil,
  omit,
  pick,
  pluck,
  propOr,
  uniq,
} from '../../utils/fp';
import * as rules from '../rules';
import {
  applyTZedFormat,
  getFrequencyFromSdmxPeriod,
  getIrregularRangeScope,
  getMinimalFreqScope,
} from './frequency';

const DEFAULT_REJECTED = new Set(['_L', '_T', '_Z']);

export const isMetadataSupported = datasource => !isNil(datasource?.urlv3);

const singleTimeSelection = value => `ge:${value}+le:${value}`;
export const rangeTimeSelection = (start, end) => {
  const startParam = start ? `ge:${start}` : null;
  const endParam = end ? `le:${end}` : null;
  if (startParam && endParam) {
    return `${startParam}+${endParam}`;
  }
  return startParam || endParam || null;
};
const isIrregularPeriod = id => {
  const match = id.match(/\/P(T?)(\d+)([YMDHS])$/);
  return match !== null;
};

export const getMetadataTimePeriodSelection = (coordinates, timeArtefact, dimensions) => {
  if (!timeArtefact || !coordinates) {
    return {};
  }
  if (!coordinates[timeArtefact.id]) {
    return null;
  }
  const timeValueId = coordinates[timeArtefact.id];
  if (!isIrregularPeriod(timeValueId)) {
    return singleTimeSelection(timeValueId);
  }
  const timeDimension = dimensions.find(d => d.id === timeArtefact.id);
  if (!timeDimension) {
    return null;
  }
  const timeValue = timeDimension.values.find(v => v.id === timeValueId);
  if (!timeValue) {
    return null;
  }
  const [sdmxStartId, rangeDef] = timeValueId.split('/');
  const idFreqFormat = getFrequencyFromSdmxPeriod(sdmxStartId);
  const rangeScope = getIrregularRangeScope(rangeDef);
  const freqFormat = getMinimalFreqScope(idFreqFormat, rangeScope);
  const sdmxStartPeriod = applyTZedFormat(freqFormat)(timeValue.start);
  const sdmxEndPeriod = applyTZedFormat(freqFormat)(timeValue.end);
  return rangeTimeSelection(sdmxStartPeriod, sdmxEndPeriod);
};

export const getPartialDataquery = (indexedDimValIds, dimensions, timeArtefactId) => {
  return dimensions
    .filter(d => d.id !== timeArtefactId)
    .map(dim => {
      const values = dim.values;
      if (values.length === 1) {
        return values[0].id;
      }
      if (!has(dim.id, indexedDimValIds)) {
        return '*';
      }
      return propOr('*', dim.id, indexedDimValIds);
    })
    .join('.');
};

export const getHeaderAdvancedAttributes = (
  attributes,
  dimensions,
  combinations,
  customAttributes,
) => {
  const indexedDimensions = indexBy(dim => dim.id, dimensions);
  const combinationsConcepts = pluck('concepts', combinations).flat();
  const rejectedIds = new Set(
    [combinationsConcepts, customAttributes.notes, customAttributes.flags].flat(),
  );
  return attributes.reduce((acc, attribute) => {
    if (!attribute.header || rejectedIds.has(attribute.id)) {
      return acc;
    }
    const value = attribute.values[0];
    if (isNil(value) || !propOr(true, 'display', value) || DEFAULT_REJECTED.has(value.id)) {
      return acc;
    }
    let ids = [];
    const coordinates = attribute.relationship.reduce((_acc, id) => {
      const dim = indexedDimensions[id];
      const dimValue = dim.values[0];
      ids = [...ids, `${dim.id}=${dimValue.id}`];
      return { ..._acc, [dim.id]: dimValue.id };
    }, {});
    const key = ids.join(':');
    const attr = {
      ...pick(['id', 'name'], attribute),
      value: attribute.values[0],
    };
    const withAttr = assocPath([key, 'attributes', attribute.id], attr, acc);
    return assocPath([key, 'coordinates'], coordinates, withAttr);
  }, {});
};

export const getSeriesAdvancedAttributes = (attributesSeries, combinations, customAttributes) => {
  const combinationsConcepts = pluck('concepts', combinations).flat();
  const nonAdvancedIds = [
    combinationsConcepts,
    customAttributes.notes || [],
    customAttributes.flags || [],
  ].flat();
  return Object.keys(attributesSeries).reduce((acc, serieKey) => {
    const serie = attributesSeries[serieKey];
    const attributes = omit(nonAdvancedIds, serie);
    if (isEmpty(attributes)) {
      return acc;
    }
    const coordinates = Object.values(attributes).find(value => !isNil(value))?.coordinates;
    return { ...acc, [serieKey]: { attributes, coordinates } };
  }, {});
};

const formatAdvancedAttributeValue = display => attr => ({
  id: attr.id,
  label: rules.dimensionValueDisplay(display)(attr),
  value: rules.dimensionValueDisplay(display)(attr.value),
});

const getSerieKey = dimensions => coordinates =>
  dimensions
    .map(dim => {
      if (!has(dim.id, coordinates)) {
        return '';
      }
      return dim.values.findIndex(v => v.id === coordinates[dim.id]);
    })
    .join(':');

export const serializeAdvancedAttributes = (attributes, display, dimensions) =>
  attributes.reduce((acc, attr) => {
    const formattedAttribute = formatAdvancedAttributeValue(display)(attr);
    const key = getSerieKey(dimensions)(attr.coordinates);

    return { ...acc, [key]: [...(acc[key] || []), formattedAttribute] };
  }, {});

const sortByCoordinates = (a, b, option = false) => {
  const splitACoord = a.splitCoord;
  const splitBCoord = b.splitCoord;
  const aWeight = splitACoord.filter(coord => !isEmpty(coord)).length;
  const bWeight = splitBCoord.filter(coord => !isEmpty(coord)).length;
  const dfWeight = !aWeight || !bWeight;

  if (aWeight !== bWeight) {
    if (option && dfWeight) {
      return aWeight - bWeight;
    }
    return bWeight - aWeight;
  }

  let ind = 0;
  while (splitACoord[ind] === splitBCoord[ind]) {
    ind++;
  }
  const _a = isEmpty(splitACoord[ind]) ? 0 : Number(splitACoord[ind]);
  const _b = isEmpty(splitBCoord[ind]) ? 0 : Number(splitBCoord[ind]);
  return _b - _a;
};

// options = { display, attributesLabel, isHeader }
export const getSidebarData = (attributesSeries, metadataSeries, dataflow, dimensions, options) => {
  const coordinates = uniq([
    ...Object.keys(attributesSeries || {}),
    ...Object.keys(metadataSeries || {}),
  ]);
  const series = coordinates.reduce((acc, coordinate) => {
    const attributes = propOr(null, coordinate, attributesSeries || {});
    const metadata = propOr(null, coordinate, metadataSeries || {});
    const children = [
      ...(isNil(attributes)
        ? []
        : [
            {
              id: `${coordinate}-attr`,
              label: options.attributesLabel,
              children: attributes,
            },
          ]),
      ...(isNil(metadata) ? [] : metadata),
    ];
    if (isEmpty(children)) {
      return acc;
    }
    const splitCoord = coordinate.split(':');
    const { split, labels } = splitCoord.reduce(
      (acc, valIndex, dimIndex) => {
        if (isEmpty(valIndex)) {
          return { ...acc, split: [...acc.split, ''] };
        }
        const dim = dimensions[dimIndex];
        const values = propOr([], 'values', dim || {});
        const value = values[Number(valIndex)];
        if (
          !value ||
          !propOr(true, 'display', dim) ||
          !propOr(true, 'display', value) ||
          DEFAULT_REJECTED.has(value.id)
        ) {
          return { ...acc, split: [...acc.split, ''] };
        }
        const dimLabel = rules.dimensionValueDisplay(options.display)(dim);
        const valLabel = rules.dimensionValueDisplay(options.display)(value);
        return {
          labels: [...acc.labels, `${dimLabel}: ${valLabel}`],
          split: [...acc.split, valIndex],
        };
      },
      { split: [], labels: [] },
    );
    const label = isEmpty(labels)
      ? rules.dimensionValueDisplay(options.display)(dataflow)
      : labels.join(' ● ');

    return [
      ...acc,
      {
        id: split.join(':'),
        splitCoord: split,
        label,
        children: children.filter(Boolean),
      },
    ];
  }, []);

  // after removed non displayed dim vals, some series might be duplicated
  const grouped = groupBy(serie => serie.id, series);
  const deduped = Object.values(grouped).map(entries => {
    if (entries.length === 1) {
      return entries[0];
    }
    const head = entries[0];
    const children = pluck('children', entries).flat();
    return { ...head, children };
  });

  return [...deduped].sort((a, b) => sortByCoordinates(a, b, options.isHeader));
};
